import React, { useCallback, useState } from 'react';

import { CustomEventNames } from 'features/analytics/constants';
import { LayoutChangeEvent, Pressable, StyleSheet } from 'react-native';

import { Coachmark } from 'components/coachmark';
import { Box, Typography } from 'components/ui';
import { t } from 'state/intl/translations';
import { getDesignToken, primitive, spacing } from 'styles/constants';

import { formatTreatmentSignupLabel } from './format-treatment-signup-label';
import { logSignupFlowRedesignEvent } from './signup-flow-redesign-analytics';

type BirthdayCoachmarkLabelProps = {
  isDisabled?: boolean;
  isRequired: boolean;
};

const COACHMARK_WIDTH = 350;
const COACHMARK_POINTER_HALF_WIDTH = 10;
const COACHMARK_DISMISS_BUTTON_SIZE = 20;

export const BirthdayCoachmarkLabel = ({ isDisabled, isRequired }: BirthdayCoachmarkLabelProps) => {
  const [isCoachmarkVisible, setIsCoachmarkVisible] = useState(false);
  const [infoButtonCenterX, setInfoButtonCenterX] = useState(COACHMARK_WIDTH / 2);

  const showCoachmark = useCallback(() => {
    logSignupFlowRedesignEvent(CustomEventNames.SIGN_UP_DATE_OF_BIRTH_COACHMARK_ICON_PRESSED);
    setIsCoachmarkVisible(true);
  }, []);

  const dismissCoachmark = useCallback(() => {
    logSignupFlowRedesignEvent(CustomEventNames.SIGN_UP_DATE_OF_BIRTH_COACHMARK_DISMISSED);
    setIsCoachmarkVisible(false);
  }, []);

  const handleInfoButtonLayout = useCallback((event: LayoutChangeEvent) => {
    const { width, x } = event.nativeEvent.layout;
    setInfoButtonCenterX(x + width / 2);
  }, []);

  const coachmarkPointerMarginLeft = Math.max(0, infoButtonCenterX - COACHMARK_POINTER_HALF_WIDTH);

  return (
    <Box style={styles.container} pointerEvents="box-none" testID="signup-dob-coachmark-label">
      <Box style={styles.labelRow} pointerEvents="box-none">
        <Typography variant="copyOne" style={styles.label}>
          {formatTreatmentSignupLabel(t('dateOfBirth', 'Date of Birth'))}
          {isRequired ? '*' : ''}
        </Typography>
        <Pressable
          accessibilityLabel={t(
            'signUpDateOfBirthCoachmarkAccessibilityLabel',
            'Learn why we ask for your birthday'
          )}
          accessibilityRole="button"
          disabled={isDisabled}
          hitSlop={8}
          onLayout={handleInfoButtonLayout}
          onPress={showCoachmark}
          style={styles.infoButton}
          testID="signup-dob-coachmark-info"
        >
          <Typography variant="copyTwo" style={styles.infoButtonText}>
            ?
          </Typography>
        </Pressable>
        {!isRequired && (
          <Typography variant="copyOne" style={styles.optionalLabel}>
            ({t('optional', 'optional')})
          </Typography>
        )}
      </Box>
      {isCoachmarkVisible && (
        <Coachmark
          body={t(
            'signUpDateOfBirthCoachmarkBody',
            'We use your birthday to personalize your BK experience, including offers and rewards.'
          )}
          onDismiss={dismissCoachmark}
          placement="bottom"
          arrowAlign="center"
          style={styles.coachmark}
          bubbleStyle={styles.coachmarkBubble}
          bodyStyle={styles.coachmarkBody}
          pointerStyle={[styles.coachmarkPointer, { marginLeft: coachmarkPointerMarginLeft }]}
          dismissButtonIconProps={{ size: '$4' }}
          dismissButtonPadding={0}
          dismissButtonPosition="top-right"
          dismissButtonSize={COACHMARK_DISMISS_BUTTON_SIZE}
          dismissButtonStyle={styles.coachmarkDismissButton}
          testID="signup-dob-coachmark"
        />
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  coachmark: {
    bottom: '100%',
    left: 0,
    marginBottom: spacing.$2,
    maxWidth: '100%',
    right: undefined,
    width: COACHMARK_WIDTH,
  },
  coachmarkBubble: {
    paddingBottom: spacing.$3,
    paddingLeft: spacing.$3,
    paddingRight: spacing.$3,
    paddingTop: spacing.$3,
  },
  coachmarkBody: {
    fontSize: primitive.fontSizes.xs,
    lineHeight: spacing.$4,
    marginRight: spacing.$8,
  },
  coachmarkDismissButton: {
    height: COACHMARK_DISMISS_BUTTON_SIZE,
    right: spacing.$2,
    top: spacing.$2,
    width: COACHMARK_DISMISS_BUTTON_SIZE,
  },
  coachmarkPointer: {
    alignSelf: 'flex-start',
  },
  container: {
    marginBottom: spacing.$2,
    position: 'relative',
    zIndex: 1,
  },
  infoButton: {
    alignItems: 'center',
    backgroundColor: primitive.$bbqBrown,
    borderRadius: 8,
    height: 16,
    justifyContent: 'center',
    width: 16,
  },
  infoButtonText: {
    color: primitive.$white,
    fontFamily: primitive.fontFamily.$body,
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 16,
  },
  label: {
    color: primitive.$bbqBrown,
    fontFamily: primitive.fontFamily.$headers,
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 18,
  },
  labelRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.$1,
  },
  optionalLabel: {
    color: getDesignToken('text-secondary'),
    fontSize: 14,
    lineHeight: 18,
  },
});
